﻿// © Microsoft Corporation. All rights reserved.

/**
 * The aria-label html attribute
 * @type {string}
 */
export const ariaLabelAttribute: string = "aria-label";

/**
 * The id attribute on the <div> element that is the root element for Peregrine Pages / Experiences.
 * There is only ever 1 root element on a page
 * @type {string}
 */
export const rootElementId: string = "msnewsroot";

/**
 * Element names that are commonly used in Peregrine pages and experiences
 * @type {object}
 */
export const htmlElementIds: any = {
    html: "html",
    head: "head",
    body: "body"
};

/**
 * Constants used by application cache related scenarios
 */
export const applicationCacheConstants = {
    /**
     * Attribute name on html tag that is set when appcache is being used on the page
     */
    manifestAttributeName: "manifest",

    /**
     * Local storage key that holds the appcache info used to determine page refresh needs
     * for different page types.
     */
    pagesRefreshData: "ac_prd",

    /**
     * Local storage key that holds the appcache stored head data entries
     */
    storedHeadDataKey: "ac_shd",

    /**
     * Local storage key that holds the appcache stored activity IDs
     */
    storedLastUsedActivityIdKey: "ac_sai"

};

/**
 * The attributes that are part of markup elements that we care about
 */
export const clientSettings = "data-client-settings";
export const pagetypeSettings = "data-pagetype";
export const id = "data-id";
export const info = "data-info";
export const js = "data-js";
export const m = "data-m";
export const lang = "lang";
export const dir = "dir";
export const serviceUrl = "data-service-url";
export const telemetryId = "data-telemetry-id";
export const trackPageProduct = "data-page-product";
export const trackPageType = "data-page-type";

/**
 * The number of characters that our Service Request Ids must have. This is currently defined by OneService
 * @type {number}
 */
export const numberOfCharactersNeededForUniqueRequestId: number = 32;

/**
 * The minimum number of path segments that will be in the URL which hosts MS News Experience pages.
 * The first path segment should be the vertical key, e.g. spartan and the second path segment
 * should be the the category key, e.g. ntp
 * @type {number}
 */
export const minimumHostPagePathSegments: number = 2;

/**
 * Default value we use for the name of the cookie that holds the ANID
 */
export const anonCookieName = "APP_ANON";

/**
 * App Anon Cookie Exists Cookie Name. This cookie is a marker if APP_ANON exist or not.
 * This is necessary because APP_ANON is HTTP cookie and not accessible by JS.
 */
export const appAnonCookieExistsCookieName = "aace";

/**
 * Child cookie name, used to signal if rpsUser is child account.
 */
export const childCookieName = "child";

/**
 * Migration consent state cookie, indicates user has already granted or denied migration consent
 */
export const migrationConsentStateKey = "migration-state";

/**
 * Id of MSCC cookie banner container element.
 */
export const msccCookieBannerElementId = "consent-banner-container";

/**
 * Headers used for one service requests
 */
export const oneServiceHeaders = {
    activityId: "x-client-activityid",
    authorization: "Authorization"
};

/**
 * used query string params in OneService requests
 */
export const oneServiceQueryStringKeys = {
    activityId: "activityId",
    apiKey: "apikey",
    complexInfoPaneEnabled: "cipenabled",
    contentType: "contentType",
    delta: "delta",
    disableTypeSerialization: "DisableTypeSerialization",
    infopaneCount: "infopaneCount",
    fdhead: "fdhead",
    feedLayoutRequestType: "t",
    filter: "$filter",
    ids: "ids",
    location: "location",
    market: "market",
    ocid: "ocid",
    parentContentId: "parent-content-id",
    parentNamespace: "parent-ns",
    parentTitle: "parent-title",
    query: "query",
    queryType: "queryType",
    qScope: "qscope",
    responseSchema: "responseschema",
    select: "$select",
    session: "session",
    signInCookieName: "scn",
    skip: "$skip",
    source: "source",
    timeOut: "timeOut",
    top: "$top",
    user: "user",
    wrapOData: "wrapodata"
};

/**
 * Constants to use as role attribute value for elements
 */
export enum AriaRoles {
    /**
     * Presentation role is used to remove semantic meaning from an element and any of its related child elements.
     */
    Presentation = "presentation"
}

/**
 * Basic enum values for mobile device orientation
 */
export enum DeviceOrientation {
    Unknown = "Unknown",
    Portrait = "Portrait",
    Landscape = "Landscape"
}

/**
 * Enterprise compliant domain for One Service api
 */
export const EnterpriseOneServiceApi = "https://ent-api.msn.com/";
